import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../../../store/store';
import {
    fetchPublishedReviews,
    fetchMyReview,
    createReview,
    updateReview,
    deleteReview,
    restoreReview,
    voteReview,
    selectMyReview,
    selectPublishedReviews,
    addReviewUpdate,
    // selectMyVote,
    type ProductReview,
    type ReviewStatus,
    type ReviewUpdateEntry,
} from '../../../../store/ReviewsSlice';
import { clearSession } from '../../../../store/AuthSlice';
import type { Order } from '../../../../types/common';
import SidePopup from '../../../shared/SidePopup/SidePopup';
import AuthPopupContent from '../../../shared/Header/authPopup/AuthPopupContent';
import './productReviews.scss';

interface ProductReviewsProps {
    className?: string;
    isMobile?: boolean;
    title?: string;
    reviews?: ProductReview[];
    defaultOpen?: boolean;
    productId?: string;
    reviewsEnabled?: boolean;
    /* Внутри таба или секции аккордеона: заголовок даёт родитель, свой не нужен */
    embedded?: boolean;
}
type EligibilityReason = 'ok' | 'disabled' | 'login' | 'need_buy' | 'wait_complete' | 'unknown';
type ReviewVote = 'up' | 'down' | null;
type SortMode = 'helpful' | 'new';
type FieldErrors = {
    name?: string;
    rating?: string;
    text?: string;
    pros?: string;
    cons?: string;
    update?: string;
};

const BASE_URL = String(process.env.REACT_APP_SERVER_URL || '').trim();

// ===== утилиты =====
const clampRating = (n: number) => {
    const v = Number.isFinite(n) ? n : 5;
    return Math.max(1, Math.min(5, Math.round(v)));
};
function normalizeStatus(s: unknown): string {
    return String(s ?? '')
        .trim()
        .toLowerCase();
}
function isCompletedStatus(status: unknown): boolean {
    return normalizeStatus(status) === 'completed';
}
function orderHasProduct(order: any, productId: string): boolean {
    const cart = Array.isArray(order?.cart) ? order.cart : [];
    return cart.some((it: any) => String(it?.productId ?? '').trim() === productId);
}
const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;
const normalizeMyStatus = (s: unknown): ReviewStatus => {
    const v = String(s ?? '')
        .trim()
        .toLowerCase();
    if (v === 'pending' || v === 'published' || v === 'rejected' || v === 'deleted') return v as ReviewStatus;
    return 'pending';
};
const formatDateRu = (iso: string) => {
    try {
        const t = Date.parse(iso);
        if (!Number.isFinite(t)) return iso;
        return new Intl.DateTimeFormat('ru-RU', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(t));
    } catch {
        return iso;
    }
};
const safeNumber = (n: unknown, fallback = 0) => (typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : fallback);
const normalizeSpaces = (s: string) => s.replace(/\s+/g, ' ').trim();
const cut = (s: string, max: number) => (s.length > max ? s.slice(0, max) : s);
const StarIcon: React.FC<{ filled: boolean; className?: string; title?: string }> = ({ filled, className, title }) => (
    <svg className={className} width="18" height="18" viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? 'img' : 'presentation'}>
        {title ? <title>{title}</title> : null}
        <path
            d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
            fill={filled ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="1.5"
        />
    </svg>
);
/* Палец вниз - тот же контур, развёрнутый на 180°, отдельный path не нужен */
const ThumbIcon: React.FC<{ down?: boolean }> = ({ down }) => (
    <svg
        className={`product-reviews__thumb${down ? ' product-reviews__thumb--down' : ''}`}
        width="15"
        height="15"
        viewBox="0 0 24 24"
        aria-hidden="true"
        role="presentation"
    >
        <path
            d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"
            fill="currentColor"
        />
    </svg>
);

const RatingStars: React.FC<{ value: number; className?: string; ariaLabel?: string }> = ({ value, className, ariaLabel }) => {
    const v = clampRating(value);
    return (
        <div className={className} aria-label={ariaLabel} role="img">
            {Array.from({ length: 5 }).map((_, i) => (
                <StarIcon key={i} filled={i < v} className="product-reviews__star" />
            ))}
        </div>
    );
};

// ===== ВАЛИДАЦИИ =====
const LIMITS = {
    nameMax: 40,
    textMin: 20,
    textMax: 1500,
    prosMax: 200,
    consMax: 200,
    updateMin: 10,
    updateMax: 600,
    updatesMax: 20,
};
function validateReviewDraft(params: { name: string; rating: number; text: string; pros: string; cons: string }): FieldErrors {
    const errs: FieldErrors = {};
    const name = normalizeSpaces(params.name || '');
    const text = normalizeSpaces(params.text || '');
    const pros = normalizeSpaces(params.pros || '');
    const cons = normalizeSpaces(params.cons || '');
    const rating = Number(params.rating);
    if (!(Number.isFinite(rating) && rating >= 1 && rating <= 5)) {
        errs.rating = 'Поставьте оценку от 1 до 5';
    }
    if (name.length > LIMITS.nameMax) errs.name = `Максимум ${LIMITS.nameMax} символов`;
    if (!text && !pros && !cons) errs.text = 'Напишите текст отзыва или укажите плюсы/минусы';
    if (text && text.length < LIMITS.textMin) errs.text = `Минимум ${LIMITS.textMin} символов`;
    if (text.length > LIMITS.textMax) errs.text = `Максимум ${LIMITS.textMax} символов`;
    if (pros.length > LIMITS.prosMax) errs.pros = `Максимум ${LIMITS.prosMax} символов`;
    if (cons.length > LIMITS.consMax) errs.cons = `Максимум ${LIMITS.consMax} символов`;
    return errs;
}
function validateUpdateDraft(textRaw: string): FieldErrors {
    const errs: FieldErrors = {};
    const t = normalizeSpaces(textRaw || '');
    if (!t) errs.update = 'Напишите текст дополнения';
    else if (t.length < LIMITS.updateMin) errs.update = `Минимум ${LIMITS.updateMin} символов`;
    else if (t.length > LIMITS.updateMax) errs.update = `Максимум ${LIMITS.updateMax} символов`;
    return errs;
}
function sanitizeReviewMaybe(raw: unknown): ProductReview | null {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as any;
    const id = cut(String(r.id ?? '').trim(), 80);
    if (!id) return null;
    const createdAtRaw = String(r.createdAt ?? '').trim();
    const createdAtMs = Date.parse(createdAtRaw);
    const createdAt = Number.isFinite(createdAtMs) ? new Date(createdAtMs).toISOString() : new Date().toISOString();
    const author = cut(normalizeSpaces(String(r.author ?? '')), LIMITS.nameMax) || 'Покупатель';
    const ratingRaw = Number(r.rating);
    const rating = clampRating(Number.isFinite(ratingRaw) ? ratingRaw : 5);
    const text = cut(normalizeSpaces(String(r.text ?? '')), LIMITS.textMax);
    const pros = cut(normalizeSpaces(String(r.pros ?? '')), LIMITS.prosMax);
    const cons = cut(normalizeSpaces(String(r.cons ?? '')), LIMITS.consMax);
    const hasAnyText = Boolean(text || pros || cons);
    if (!hasAnyText) return null;
    const status = normalizeMyStatus(r.status);
    const rejectionReason = r.rejectionReason == null ? null : cut(normalizeSpaces(String(r.rejectionReason)), 300);
    const deletedAtRaw = r.deletedAt == null ? '' : String(r.deletedAt).trim();
    const deletedAtMs = deletedAtRaw ? new Date(deletedAtRaw).getTime() : NaN;
    let deletedAt = Number.isFinite(deletedAtMs) ? new Date(deletedAtMs).toISOString() : null;
    if (status === 'deleted' && !deletedAt) deletedAt = new Date().toISOString();
    const updatesRaw = Array.isArray(r.updates) ? r.updates : [];
    const updates: ReviewUpdateEntry[] = updatesRaw
        .slice(0, LIMITS.updatesMax)
        .map((u: any) => {
            const t = cut(normalizeSpaces(String(u?.text ?? '')), LIMITS.updateMax);
            const caRaw = String(u?.createdAt ?? '').trim();
            const caMs = new Date(caRaw).getTime();
            return {
                id: String(u?.id ?? '').trim() || makeId(),
                text: t,
                createdAt: Number.isFinite(caMs) ? new Date(caMs).toISOString() : new Date().toISOString(),
            };
        })
        .filter((u: ReviewUpdateEntry) => Boolean(u.text));
    const isVerifiedPurchase = Boolean(r.isVerifiedPurchase);
    return {
        id,
        author,
        rating,
        text,
        createdAt,
        pros: pros || undefined,
        cons: cons || undefined,
        isVerifiedPurchase,
        updates,
        status,
        rejectionReason,
        deletedAt,
        helpfulCount: safeNumber(r.helpfulCount, 0),
        unhelpfulCount: safeNumber(r.unhelpfulCount, 0),
    };
}

const ProductReviews: React.FC<ProductReviewsProps> = ({
    className,
    isMobile = false,
    title = 'Отзывы',
    reviews = [],
    defaultOpen = true,
    productId,
    reviewsEnabled = true,
    embedded = false,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
    const profileFirstName = useMemo(() => normalizeSpaces(String(user?.firstName ?? '')), [user]);
    const profileLastName = useMemo(() => normalizeSpaces(String(user?.lastName ?? '')), [user]);
    const profileAuthor = useMemo(() => {
        if (!profileFirstName) return '';
        const li = profileLastName ? profileLastName[0].toUpperCase() : '';
        return li ? `${profileFirstName} ${li}.` : profileFirstName;
    }, [profileFirstName, profileLastName]);
    const isNameLocked = Boolean(profileFirstName);
    const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
    const [eligLoading, setEligLoading] = useState<boolean>(false);
    const [eligReason, setEligReason] = useState<EligibilityReason>('unknown');
    const publishedFromStore = useSelector((s: RootState) => (productId ? selectPublishedReviews(s, productId) : []));
    const myReview = useSelector((s: RootState) => (productId ? selectMyReview(s, productId) : null));
    const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
    const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
    const [isUpdateFormOpen, setIsUpdateFormOpen] = useState<boolean>(false);
    const [draftName, setDraftName] = useState<string>('');
    const [draftRating, setDraftRating] = useState<number>(0);
    const [draftText, setDraftText] = useState<string>('');
    const [draftPros, setDraftPros] = useState<string>('');
    const [draftCons, setDraftCons] = useState<string>('');
    const [editName, setEditName] = useState<string>('');
    const [editRating, setEditRating] = useState<number>(5);
    const [editText, setEditText] = useState<string>('');
    const [editPros, setEditPros] = useState<string>('');
    const [editCons, setEditCons] = useState<string>('');
    const [draftUpdateText, setDraftUpdateText] = useState<string>('');
    const [createErrors, setCreateErrors] = useState<FieldErrors>({});
    const [editErrors, setEditErrors] = useState<FieldErrors>({});
    const [updateErrors, setUpdateErrors] = useState<FieldErrors>({});
    const [createTouched, setCreateTouched] = useState(false);
    const [editTouched, setEditTouched] = useState(false);
    const [updateTouched, setUpdateTouched] = useState(false);
    const [sortMode, setSortMode] = useState<SortMode>('helpful');
    const [voteBusyIds, setVoteBusyIds] = useState<Record<string, boolean>>({});
    const [authPopupOpen, setAuthPopupOpen] = useState(false);
    const closeAuthPopup = useCallback(() => setAuthPopupOpen(false), []);
    const openAuthPopup = useCallback(() => setAuthPopupOpen(true), []);

    useEffect(() => {
        if (!productId) return;
        dispatch(fetchPublishedReviews({ productId, sort: sortMode }));
        if (isAuthenticated) dispatch(fetchMyReview({ productId }));
    }, [dispatch, productId, isAuthenticated, sortMode]);

    const hasMyReview = Boolean(myReview);
    const myStatus: ReviewStatus | null = myReview ? normalizeMyStatus(myReview.status) : null;

    useEffect(() => {
        let ignore = false;
        const ac = new AbortController();
        const run = async () => {
            if (hasMyReview) {
                if (!ignore) setEligReason('unknown');
                return;
            }
            if (!reviewsEnabled) {
                if (!ignore) setEligReason('disabled');
                return;
            }
            if (!productId) {
                if (!ignore) setEligReason('unknown');
                return;
            }
            if (!BASE_URL) {
                if (!ignore) setEligReason('unknown');
                return;
            }
            if (!isAuthenticated) {
                if (!ignore) setEligReason('login');
                return;
            }
            setEligLoading(true);
            try {
                const resp = await fetch(`${BASE_URL}/orders/my`, {
                    method: 'GET',
                    credentials: 'include',
                    signal: ac.signal,
                });
                if (resp.status === 401) {
                    dispatch(clearSession());
                    if (!ignore) setEligReason('login');
                    return;
                }
                if (!resp.ok) {
                    if (!ignore) setEligReason((prev) => (prev === 'need_buy' || prev === 'wait_complete' ? prev : 'unknown'));
                    return;
                }
                const data = await resp.json();
                const orders: Order[] = Array.isArray(data)
                    ? data
                    : Array.isArray((data as any)?.orders)
                      ? (data as any).orders
                      : Array.isArray((data as any)?.items)
                        ? (data as any).items
                        : [];
                const withProduct = orders.filter((o: any) => orderHasProduct(o, productId));
                const completedWithProduct = withProduct.some((o: any) => isCompletedStatus(o?.orderStatus));
                const hasAnyWithProduct = withProduct.length > 0;
                const hasNonCompletedWithProduct = hasAnyWithProduct && !completedWithProduct;
                if (!ignore) {
                    if (completedWithProduct) setEligReason('ok');
                    else if (hasNonCompletedWithProduct) setEligReason('wait_complete');
                    else setEligReason('need_buy');
                }
            } catch (e: any) {
                // abort - не считаем ошибкой
                if (e?.name === 'AbortError') return;
                if (!ignore) setEligReason('unknown');
            } finally {
                if (!ignore) setEligLoading(false);
            }
        };
        run();
        return () => {
            ignore = true;
            ac.abort();
        };
    }, [isAuthenticated, productId, reviewsEnabled, dispatch, hasMyReview]);

    const canCreateReview = Boolean(!hasMyReview && eligReason === 'ok');
    const canManageMyReview = Boolean(isAuthenticated && productId && reviewsEnabled);
    const canEditMyReview = Boolean(myReview && myStatus && (myStatus === 'pending' || myStatus === 'rejected') && canManageMyReview);
    const canAddUpdate = Boolean(myReview && myStatus === 'published' && canManageMyReview);
    const canDeleteMyReview = Boolean(myReview && myStatus !== 'deleted' && canManageMyReview);
    const canRestoreMyReview = Boolean(myReview && myStatus === 'deleted' && canManageMyReview);

    useEffect(() => {
        if (!isAuthenticated) {
            if (isFormOpen) setIsFormOpen(false);
            if (isEditOpen) setIsEditOpen(false);
            if (isUpdateFormOpen) setIsUpdateFormOpen(false);
        }
    }, [isAuthenticated, isFormOpen, isEditOpen, isUpdateFormOpen]);

    useEffect(() => {
        if (hasMyReview && isFormOpen) setIsFormOpen(false);
    }, [hasMyReview, isFormOpen]);

    useEffect(() => {
        if (!canEditMyReview && isEditOpen) setIsEditOpen(false);
        if (!canAddUpdate && isUpdateFormOpen) setIsUpdateFormOpen(false);
    }, [canEditMyReview, canAddUpdate, isEditOpen, isUpdateFormOpen]);

    useEffect(() => {
        if (!isEditOpen || !myReview) return;
        const sanitized = sanitizeReviewMaybe(myReview) || myReview;
        setEditName(isNameLocked ? profileAuthor : sanitized.author || '');
        setEditRating(clampRating(sanitized.rating || 5));
        setEditText(sanitized.text || '');
        setEditPros(sanitized.pros || '');
        setEditCons(sanitized.cons || '');
        setEditTouched(false);
        setEditErrors({});
    }, [isEditOpen, myReview, isNameLocked, profileAuthor]);

    useEffect(() => {
        if (!isNameLocked) return;
        setDraftName(profileAuthor);
        if (isEditOpen) setEditName(profileAuthor);
    }, [isNameLocked, profileAuthor, isEditOpen]);

    useEffect(() => {
        if (!isFormOpen) {
            setCreateTouched(false);
            setCreateErrors({});
        }
    }, [isFormOpen]);

    useEffect(() => {
        if (!isUpdateFormOpen) {
            setUpdateTouched(false);
            setUpdateErrors({});
        }
    }, [isUpdateFormOpen]);

    useEffect(() => {
        if (!isFormOpen) return;
        const nameForValidate = isNameLocked ? profileAuthor : draftName;
        setCreateErrors(validateReviewDraft({ name: nameForValidate, rating: draftRating, text: draftText, pros: draftPros, cons: draftCons }));
    }, [isFormOpen, draftName, draftRating, draftText, draftPros, draftCons, isNameLocked, profileAuthor]);

    useEffect(() => {
        if (!isEditOpen) return;
        const nameForValidate = isNameLocked ? profileAuthor : editName;
        setEditErrors(validateReviewDraft({ name: nameForValidate, rating: editRating, text: editText, pros: editPros, cons: editCons }));
    }, [isEditOpen, editName, editRating, editText, editPros, editCons, isNameLocked, profileAuthor]);

    useEffect(() => {
        if (!isUpdateFormOpen) return;
        setUpdateErrors(validateUpdateDraft(draftUpdateText));
    }, [isUpdateFormOpen, draftUpdateText]);

    const createIsValid = useMemo(() => Object.keys(createErrors).length === 0, [createErrors]);
    const editIsValid = useMemo(() => Object.keys(editErrors).length === 0, [editErrors]);
    const updateIsValid = useMemo(() => Object.keys(updateErrors).length === 0, [updateErrors]);

    const statusLabel = (status: ReviewStatus): { text: string; className: string; hint: string } => {
        if (status === 'pending') {
            return {
                text: 'На модерации',
                className: 'product-reviews__badge--pending',
                hint: 'Проверка обычно занимает некоторое время. Пока отзыв не опубликован - его видите только вы.',
            };
        }
        if (status === 'published') {
            return {
                text: 'Опубликован',
                className: 'product-reviews__badge--published',
                hint: 'Отзыв виден всем. Редактирование недоступно - можно только добавить дополнение.',
            };
        }
        if (status === 'rejected') {
            return {
                text: 'Отклонён',
                className: 'product-reviews__badge--rejected',
                hint: 'Вы можете отредактировать отзыв и отправить снова на модерацию.',
            };
        }
        return {
            text: 'Удалён',
            className: 'product-reviews__badge--deleted',
            hint: 'Отзыв скрыт от других пользователей. Вы можете восстановить его (после этого он снова будет на модерации).',
        };
    };

    const renderMyStatusBadge = (status: ReviewStatus) => {
        const s = statusLabel(status);
        return <span className={`product-reviews__badge ${s.className}`}>{s.text}</span>;
    };

    const renderPrimaryActionForNonEligible = () => {
        if (hasMyReview) return null;
        if (eligLoading) {
            return (
                <button type="button" className="product-reviews__btn product-reviews__btn--primary" disabled title="Проверяем возможность оставить отзыв…">
                    Проверяем…
                </button>
            );
        }
        if (eligReason === 'login') {
            return (
                <button onClick={openAuthPopup} className="product-reviews__btn product-reviews__btn--primary">
                    Войти, чтобы оставить отзыв
                </button>
            );
        }
        if (eligReason === 'need_buy') {
            return (
                <Link to="/catalog" className="product-reviews__btn product-reviews__btn--primary" title="Отзывы доступны только покупателям">
                    Перейти к покупкам
                </Link>
            );
        }
        if (eligReason === 'wait_complete') {
            return (
                <Link to="/account" className="product-reviews__btn product-reviews__btn--primary" title="Отзыв доступен после статуса “Завершён”">
                    Мои заказы
                </Link>
            );
        }
        if (eligReason === 'disabled') {
            return (
                <button type="button" className="product-reviews__btn product-reviews__btn--primary" disabled title="Отзывы для товара отключены">
                    Отзывы отключены
                </button>
            );
        }
        return (
            <button type="button" className="product-reviews__btn product-reviews__btn--primary" disabled title="Сейчас нельзя оставить отзыв">
                Оставить отзыв
            </button>
        );
    };

    /* Кнопки действия: одна точка правды для шапки и пустого состояния */
    const renderActions = () => (
        <div className="product-reviews__action-row">
                                    {hasMyReview ? (
                                        <>
                                            {canEditMyReview ? (
                                                <button
                                                    type="button"
                                                    className="product-reviews__btn product-reviews__btn--primary"
                                                    onClick={() => setIsEditOpen((v) => !v)}
                                                    aria-expanded={isEditOpen}
                                                    disabled={!canEditMyReview}
                                                >
                                                    Редактировать
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="product-reviews__btn product-reviews__btn--primary"
                                                    onClick={() => setIsUpdateFormOpen((v) => !v)}
                                                    aria-expanded={isUpdateFormOpen}
                                                    disabled={!canAddUpdate}
                                                    title={!canAddUpdate ? 'Дополнить можно только опубликованный отзыв' : undefined}
                                                >
                                                    Дополнить
                                                </button>
                                            )}

                                            {canDeleteMyReview && (
                                                <button type="button" className="product-reviews__btn" onClick={handleSoftDeleteMyReview}>
                                                    Удалить
                                                </button>
                                            )}
                                            {canRestoreMyReview && (
                                                <button type="button" className="product-reviews__btn" onClick={handleRestoreMyReview}>
                                                    Восстановить
                                                </button>
                                            )}
                                        </>
                                    ) : canCreateReview ? (
                                        <button
                                            type="button"
                                            className="product-reviews__btn product-reviews__btn--primary"
                                            onClick={() => setIsFormOpen((v) => !v)}
                                            aria-expanded={isFormOpen}
                                        >
                                            Оставить отзыв
                                        </button>
                                    ) : (
                                        renderPrimaryActionForNonEligible()
                                    )}
                                    {!hasMyReview && eligReason === 'need_buy' && (
                                        <Link to="/account" className="product-reviews__btn" title="Посмотреть статус заказов">
                                            Мои заказы
                                        </Link>
                                    )}
        </div>
    );

    /* Пояснение под кнопкой вместо отдельной карточки, которая дублировала призыв */
    const renderEligibilityHint = (): string | null => {
        if (hasMyReview) return null;
        if (eligLoading) return null;
        if (!reviewsEnabled || eligReason === 'disabled') return 'Для этого товара публикация отзывов сейчас недоступна.';
        if (eligReason === 'login') return 'Отзывы могут оставлять только авторизованные покупатели.';
        if (eligReason === 'need_buy') return 'Мы публикуем отзывы только от реальных покупателей.';
        if (eligReason === 'wait_complete') return 'Отзыв можно оставить, когда заказ получит статус «Завершён».';
        return 'Поделитесь опытом - это помогает другим.';
    };


    const renderFieldError = (msg?: string, show?: boolean) => {
        if (!msg || !show) return null;
        return <div className="product-reviews__field-error">{msg}</div>;
    };
    const renderCounter = (value: string, max: number) => {
        const len = (value || '').length;
        return (
            <div className="product-reviews__counter" aria-live="polite">
                {len}/{max}
            </div>
        );
    };

    const renderReviewUpdates = (updates?: ReviewUpdateEntry[]) => {
        const list = Array.isArray(updates) ? updates : [];
        if (!list.length) return null;
        return (
            <div className="product-reviews__updates">
                <div className="product-reviews__updates-title">Дополнения</div>
                {list.map((u) => (
                    <div key={u.id} className="product-reviews__update">
                        <time className="product-reviews__update-date" dateTime={u.createdAt}>
                            {formatDateRu(u.createdAt)}
                        </time>
                        <div className="product-reviews__update-text">{u.text}</div>
                    </div>
                ))}
            </div>
        );
    };

    const myVotes = useSelector((s: RootState) => s.reviews.myVotes);
    const renderHelpfulness = (review: ProductReview, vote: ReviewVote, rating: boolean) => {
        const st = {
            helpful: safeNumber((review as any)?.helpfulCount, 0),
            unhelpful: safeNumber((review as any)?.unhelpfulCount, 0),
        };
        const upActive = vote === 'up';
        const downActive = vote === 'down';
        const disableVotes = !isAuthenticated;

        return (
            <div className="product-reviews__helpfulness" aria-label="Оценка полезности отзыва">
                <span className="product-reviews__helpfulness-label">{rating ? 'Рейтинг' : 'Полезен?'}</span>
                <button
                    type="button"
                    className={`product-reviews__vote-btn ${upActive ? 'product-reviews__vote-btn--active' : ''}`}
                    onClick={async () => {
                        setVoteBusyIds((prev) => ({ ...prev, [review.id]: true }));
                        try {
                            await dispatch(voteReview({ reviewId: review.id, vote: 'up' }));
                        } finally {
                            setVoteBusyIds((prev) => ({ ...prev, [review.id]: false }));
                        }
                    }}
                    disabled={disableVotes || Boolean(voteBusyIds[review.id])}
                    aria-pressed={upActive}
                    title={disableVotes ? 'Войдите, чтобы голосовать' : 'Полезно'}
                >
                    <ThumbIcon />
                    <span className="product-reviews__vote-count">{st.helpful}</span>
                </button>
                <button
                    type="button"
                    className={`product-reviews__vote-btn ${downActive ? 'product-reviews__vote-btn--active' : ''}`}
                    onClick={async () => {
                        setVoteBusyIds((prev) => ({ ...prev, [review.id]: true }));
                        try {
                            await dispatch(voteReview({ reviewId: review.id, vote: 'down' }));
                        } finally {
                            setVoteBusyIds((prev) => ({ ...prev, [review.id]: false }));
                        }
                    }}
                    disabled={disableVotes || Boolean(voteBusyIds[review.id])}
                    aria-pressed={downActive}
                    title={disableVotes ? 'Войдите, чтобы голосовать' : 'Не полезно'}
                >
                    <ThumbIcon down />
                    <span className="product-reviews__vote-count">{st.unhelpful}</span>
                </button>
            </div>
        );
    };

    const publicReviewsRaw = useMemo(() => {
        const src = publishedFromStore.length ? publishedFromStore : Array.isArray(reviews) ? reviews : [];
        return src.filter((r) => normalizeMyStatus((r as any)?.status ?? 'published') === 'published');
    }, [publishedFromStore, reviews]);

    const { avg, count, distribution } = useMemo(() => {
        const c = publicReviewsRaw.length;
        if (!c) {
            return { avg: 0, count: 0, distribution: [0, 0, 0, 0, 0] as number[] };
        }
        let sum = 0;
        const dist = [0, 0, 0, 0, 0];
        for (const r of publicReviewsRaw) {
            const rr = clampRating(r.rating);
            sum += rr;
            dist[rr - 1] += 1;
        }
        return {
            avg: Math.round((sum / c) * 10) / 10,
            count: c,
            distribution: dist,
        };
    }, [publicReviewsRaw]);

    const timeFor = useCallback((iso: string) => {
        const t = Date.parse(iso);
        return Number.isFinite(t) ? t : 0;
    }, []);

    const scoreFor = useCallback((r: ProductReview) => {
        const helpful = safeNumber((r as any)?.helpfulCount, 0);
        const unhelpful = safeNumber((r as any)?.unhelpfulCount, 0);
        return helpful - unhelpful;
    }, []);

    const publicReviews = useMemo(() => {
        const arr = publicReviewsRaw.slice();
        if (sortMode === 'new') {
            arr.sort((a, b) => timeFor(b.createdAt) - timeFor(a.createdAt));
            return arr;
        }
        arr.sort((a, b) => {
            const s = scoreFor(b) - scoreFor(a);
            if (s !== 0) return s;
            return timeFor(b.createdAt) - timeFor(a.createdAt);
        });
        return arr;
    }, [publicReviewsRaw, timeFor, sortMode, scoreFor]);

    // ===== submit handlers (мок) =====
    const onSubmitCreateFake = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canCreateReview) return;
        setCreateTouched(true);
        const nameForValidate = isNameLocked ? profileAuthor : draftName;
        const errs = validateReviewDraft({ name: nameForValidate, rating: draftRating, text: draftText, pros: draftPros, cons: draftCons });
        setCreateErrors(errs);
        if (Object.keys(errs).length > 0) return;
        const authorRaw = isNameLocked ? profileAuthor : draftName;
        const author = cut(normalizeSpaces(authorRaw || ''), LIMITS.nameMax) || 'Покупатель';
        const text = cut(normalizeSpaces(draftText || ''), LIMITS.textMax);
        const pros = cut(normalizeSpaces(draftPros || ''), LIMITS.prosMax);
        const cons = cut(normalizeSpaces(draftCons || ''), LIMITS.consMax);
        if (!productId) return;
        const rating = draftRating;
        if (!(Number.isFinite(rating) && rating >= 1 && rating <= 5)) return;
        const a = await dispatch(
            createReview({
                productId,
                payload: { author, rating, text, pros: pros || undefined, cons: cons || undefined },
            }),
        );
        if ((a as any)?.meta?.requestStatus !== 'fulfilled') {
            const payload = String((a as any)?.payload || '');
            if (payload.startsWith('need_buy:')) setEligReason('need_buy');
            else if (payload.startsWith('wait_complete:')) setEligReason('wait_complete');
            return;
        }
        if (isAuthenticated) await dispatch(fetchMyReview({ productId }));
        setDraftName(isNameLocked ? profileAuthor : '');
        setDraftRating(0);
        setDraftText('');
        setDraftPros('');
        setDraftCons('');
        setIsFormOpen(false);
        setIsOpen(true);
    };

    const onSubmitEditFake = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canEditMyReview || !myReview) return;
        setEditTouched(true);
        const nameForValidate = isNameLocked ? profileAuthor : editName;
        const errs = validateReviewDraft({ name: nameForValidate, rating: editRating, text: editText, pros: editPros, cons: editCons });
        setEditErrors(errs);
        if (Object.keys(errs).length > 0) return;
        const authorRaw = isNameLocked ? profileAuthor : editName;
        const author = cut(normalizeSpaces(authorRaw || ''), LIMITS.nameMax) || 'Покупатель';
        const text = cut(normalizeSpaces(editText || ''), LIMITS.textMax);
        const pros = cut(normalizeSpaces(editPros || ''), LIMITS.prosMax);
        const cons = cut(normalizeSpaces(editCons || ''), LIMITS.consMax);
        if (!productId) return;
        const rating = editRating;
        if (!(Number.isFinite(rating) && rating >= 1 && rating <= 5)) return;
        const a = await dispatch(
            updateReview({
                reviewId: myReview.id,
                productId,
                payload: { author, rating, text, pros: pros || undefined, cons: cons || undefined },
            }),
        );
        if ((a as any)?.meta?.requestStatus !== 'fulfilled') return;
        await dispatch(fetchMyReview({ productId }));
        setIsEditOpen(false);
        setIsOpen(true);
    };

    const onSubmitUpdateFake = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canAddUpdate || !myReview || !productId) return;
        setUpdateTouched(true);
        const errs = validateUpdateDraft(draftUpdateText);
        setUpdateErrors(errs);
        if (Object.keys(errs).length > 0) return;
        const text = cut(normalizeSpaces(draftUpdateText || ''), LIMITS.updateMax);
        const a = await dispatch(addReviewUpdate({ reviewId: myReview.id, productId, text }));
        if ((a as any)?.meta?.requestStatus !== 'fulfilled') return;
        await dispatch(fetchMyReview({ productId }));
        setDraftUpdateText('');
        setIsUpdateFormOpen(false);
        setIsOpen(true);
    };

    const handleSoftDeleteMyReview = async () => {
        if (!canDeleteMyReview || !myReview || !productId) return;
        const a = await dispatch(deleteReview({ productId, reviewId: myReview.id }));
        if ((a as any)?.meta?.requestStatus !== 'fulfilled') return;
        await dispatch(fetchMyReview({ productId }));
        setIsEditOpen(false);
        setIsUpdateFormOpen(false);
        setIsFormOpen(false);
        setIsOpen(true);
    };

    const handleRestoreMyReview = async () => {
        if (!canRestoreMyReview || !myReview || !productId) return;
        const a = await dispatch(restoreReview({ productId, reviewId: myReview.id }));
        if ((a as any)?.meta?.requestStatus !== 'fulfilled') return;
        await dispatch(fetchMyReview({ productId }));
        setIsOpen(true);
    };

    const sectionClass = embedded
        ? ['product-reviews', 'product-reviews--embedded', className || ''].filter(Boolean).join(' ')
        : [
              'product-page__section',
              isOpen ? 'product-page__section--active' : '',
              isMobile ? 'product-page__section--mobile' : '',
              'product-reviews',
              className || '',
          ]
              .filter(Boolean)
              .join(' ');

    return (
        <div className={sectionClass}>
            {!embedded && (
                <button
                    type="button"
                    className="product-page__section-title product-reviews__title-btn"
                    onClick={() => setIsOpen((v) => !v)}
                    aria-expanded={isOpen}
                >
                    {title}
                    <span className={`product-page__section-arrow ${isOpen ? 'product-page__section-arrow--open' : ''}`} aria-hidden="true">
                        &#8249;
                    </span>
                </button>
            )}

            <div className="product-reviews__body">
                {count > 0 && (
                    <header className="product-reviews__head">
                        <div className="product-reviews__score">
                            <span className="product-reviews__avg-num">{avg.toFixed(1)}</span>
                            <span className="product-reviews__score-sub">
                                <RatingStars value={Math.round(avg)} className="product-reviews__stars" ariaLabel={`Средняя оценка ${avg} из 5`} />
                                <span className="product-reviews__count">
                                    {count} {count === 1 ? 'отзыв' : count < 5 ? 'отзыва' : 'отзывов'}
                                </span>
                            </span>
                        </div>
                        <div className="product-reviews__rating-bars" aria-label="Распределение оценок">
                            {[5, 4, 3, 2, 1].map((stars) => {
                                const val = distribution[stars - 1] || 0;
                                const percent = count ? Math.round((val / count) * 100) : 0;
                                return (
                                    <div key={stars} className="product-reviews__bar-row">
                                        <div className="product-reviews__bar-left">
                                            <span className="product-reviews__bar-stars">{stars}</span>
                                            <StarIcon filled className="product-reviews__bar-star" />
                                        </div>
                                        <div className="product-reviews__bar-track" aria-hidden="true">
                                            <div className="product-reviews__bar-fill" style={{ width: `${percent}%` }} />
                                        </div>
                                        <div className="product-reviews__bar-right">{val}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="product-reviews__action">
                            {renderActions()}
                            {renderEligibilityHint() && <span className="product-reviews__hint">{renderEligibilityHint()}</span>}
                        </div>
                    </header>
                )}
                <div className="product-reviews__stack">
                    <div className="product-reviews__own">
                        {/* ПАНЕЛЬ АВТОРА: "Ваш отзыв" */}
                        {hasMyReview && myReview && myStatus && (
                            <div className="product-reviews__my">
                                <article className="product-reviews__card">
                                    <div className="product-reviews__card-head">
                                        <div className="product-reviews__author">
                                            <span className="product-reviews__author-name">{myReview.author}</span>
                                            {/* {myReview.isVerifiedPurchase && <span className="product-reviews__badge">Покупка подтверждена</span>} */}
                                            {myReview.isVerifiedPurchase && <span className="product-reviews__badge">Ваш отзыв</span>}
                                            {renderMyStatusBadge(myStatus)}
                                        </div>
                                        <div className="product-reviews__right">
                                            <RatingStars value={myReview.rating} className="product-reviews__stars" ariaLabel={`Оценка ${myReview.rating} из 5`} />
                                            <time className="product-reviews__date" dateTime={myReview.createdAt}>
                                                {formatDateRu(myReview.createdAt)}
                                            </time>
                                        </div>
                                    </div>
                                    <div
                                        className={`product-reviews__status-note ${myStatus === 'rejected' || myStatus === 'deleted' ? 'product-reviews__status-note--warn' : ''}`}
                                    >
                                        {statusLabel(myStatus).hint}
                                        {myStatus === 'rejected' &&
                                            (myReview.rejectionReason ? ` Причина: ${myReview.rejectionReason}` : ' Причина: будет указана после модерации.')}
                                        {myStatus === 'deleted' && (myReview.deletedAt ? ` (${formatDateRu(myReview.deletedAt)})` : '')}
                                    </div>
                                    {(myReview.pros || myReview.cons) && (
                                        <div className="product-reviews__pc">
                                            {myReview.pros && (
                                                <div className="product-reviews__pc-row">
                                                    <span className="product-reviews__pc-label">Плюсы:</span>
                                                    <span className="product-reviews__pc-value">{myReview.pros}</span>
                                                </div>
                                            )}
                                            {myReview.cons && (
                                                <div className="product-reviews__pc-row">
                                                    <span className="product-reviews__pc-label">Минусы:</span>
                                                    <span className="product-reviews__pc-value">{myReview.cons}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="product-reviews__text">{myReview.text}</div>
                                    {myReview.shopReply ? (
                                        <div className="product-reviews__shop-reply">
                                            <div className="product-reviews__shop-reply-title">
                                                Ответ магазина <span className="product-reviews__shop-reply-date">{formatDateRu(myReview.shopReply.createdAt)}</span>
                                            </div>
                                            <div className="product-reviews__shop-reply-text">{myReview.shopReply.text}</div>
                                        </div>
                                    ) : null}
                                    {renderReviewUpdates(myReview.updates)}
                                    {myStatus === 'published' ? renderHelpfulness(myReview, myVotes[myReview.id] ?? null, true) : null}
                                </article>
                            </div>
                        )}
                        {/* Создание отзыва */}
                        {isFormOpen && canCreateReview && (
                            <form className="product-reviews__form" onSubmit={onSubmitCreateFake} onChange={() => !createTouched && setCreateTouched(true)}>
                                <div className="product-reviews__form-title">Ваш отзыв</div>
                                <div className="product-reviews__form-note">Что понравилось? Что можно улучшить? Чем конкретнее - тем полезнее.</div>

                                <div className="product-reviews__grid">
                                    <label className="product-reviews__field">
                                        <span className="product-reviews__label">Имя</span>
                                        <input
                                            className="product-reviews__input"
                                            value={isNameLocked ? profileAuthor : draftName}
                                            onChange={isNameLocked ? undefined : (e) => setDraftName(cut(e.target.value, LIMITS.nameMax))}
                                            placeholder={isNameLocked ? 'Имя берётся из профиля' : 'Например: Анна'}
                                            readOnly={isNameLocked}
                                            disabled={isNameLocked}
                                        />
                                        {renderCounter(isNameLocked ? profileAuthor : draftName, LIMITS.nameMax)}
                                        {renderFieldError(createErrors.name, createTouched)}
                                    </label>

                                    <label className="product-reviews__field">
                                        <span className="product-reviews__label">Оценка</span>
                                        <div
                                            className={`product-reviews__rating-pick ${createTouched && createErrors.rating ? 'product-reviews__rating-pick--error' : ''}`}
                                        >
                                            {Array.from({ length: 5 }).map((_, i) => {
                                                const v = i + 1;
                                                const active = v <= draftRating;
                                                return (
                                                    <button
                                                        key={v}
                                                        type="button"
                                                        className={`product-reviews__rate-btn ${active ? 'product-reviews__rate-btn--active' : ''}`}
                                                        onClick={() => {
                                                            setDraftRating(v);
                                                            if (!createTouched) setCreateTouched(true);
                                                        }}
                                                        aria-label={`Поставить ${v} из 5`}
                                                    >
                                                        <StarIcon filled={active} className="product-reviews__rate-star" />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {renderFieldError(createErrors.rating, createTouched)}
                                    </label>

                                    <label className="product-reviews__field product-reviews__field--wide">
                                        <span className="product-reviews__label">Текст</span>
                                        <textarea
                                            className="product-reviews__textarea"
                                            value={draftText}
                                            onChange={(e) => setDraftText(cut(e.target.value, LIMITS.textMax))}
                                            placeholder={`Минимум ${LIMITS.textMin} символов. Например: “Понравилась плотность и качество шва…”`}
                                            rows={4}
                                        />
                                        {renderCounter(draftText, LIMITS.textMax)}
                                        {renderFieldError(createErrors.text, createTouched)}
                                    </label>

                                    <label className="product-reviews__field">
                                        <span className="product-reviews__label">Плюсы</span>
                                        <input
                                            className="product-reviews__input"
                                            value={draftPros}
                                            onChange={(e) => setDraftPros(cut(e.target.value, LIMITS.prosMax))}
                                            placeholder="Например: мягкий, приятный на ощупь"
                                        />
                                        {renderCounter(draftPros, LIMITS.prosMax)}
                                        {renderFieldError(createErrors.pros, createTouched)}
                                    </label>

                                    <label className="product-reviews__field">
                                        <span className="product-reviews__label">Минусы</span>
                                        <input
                                            className="product-reviews__input"
                                            value={draftCons}
                                            onChange={(e) => setDraftCons(cut(e.target.value, LIMITS.consMax))}
                                            placeholder="Например: не нашёл"
                                        />
                                        {renderCounter(draftCons, LIMITS.consMax)}
                                        {renderFieldError(createErrors.cons, createTouched)}
                                    </label>
                                </div>

                                <div className="product-reviews__form-actions">
                                    <button
                                        type="submit"
                                        className="product-reviews__btn product-reviews__btn--primary"
                                        disabled={!createIsValid}
                                        title={!createIsValid ? 'Проверьте поля формы' : undefined}
                                    >
                                        Отправить
                                    </button>
                                    <button type="button" className="product-reviews__btn" onClick={() => setIsFormOpen(false)}>
                                        Отмена
                                    </button>
                                    <div className="product-reviews__form-note">Отзыв будет отправлен на модерацию.</div>
                                </div>
                            </form>
                        )}
                        {/* Редактирование */}
                        {isEditOpen && canEditMyReview && myReview && (
                            <form className="product-reviews__form" onSubmit={onSubmitEditFake} onChange={() => !editTouched && setEditTouched(true)}>
                                <div className="product-reviews__form-title">Редактирование отзыва</div>
                                <div className="product-reviews__form-note">
                                    Можно редактировать только пока отзыв не опубликован. После сохранения он снова уйдёт на модерацию.
                                </div>{' '}
                                <div className="product-reviews__grid">
                                    <label className="product-reviews__field">
                                        <span className="product-reviews__label">Имя</span>
                                        <input
                                            className="product-reviews__input"
                                            value={isNameLocked ? profileAuthor : editName}
                                            onChange={isNameLocked ? undefined : (e) => setEditName(cut(e.target.value, LIMITS.nameMax))}
                                            placeholder={isNameLocked ? 'Имя берётся из профиля' : 'Например: Анна'}
                                            readOnly={isNameLocked}
                                            disabled={isNameLocked}
                                        />
                                        {renderCounter(isNameLocked ? profileAuthor : editName, LIMITS.nameMax)}
                                        {renderFieldError(editErrors.name, editTouched)}
                                    </label>

                                    <label className="product-reviews__field">
                                        <span className="product-reviews__label">Оценка</span>
                                        <div
                                            className={`product-reviews__rating-pick ${editTouched && editErrors.rating ? 'product-reviews__rating-pick--error' : ''}`}
                                        >
                                            {Array.from({ length: 5 }).map((_, i) => {
                                                const v = i + 1;
                                                const active = v <= editRating;
                                                return (
                                                    <button
                                                        key={v}
                                                        type="button"
                                                        className={`product-reviews__rate-btn ${active ? 'product-reviews__rate-btn--active' : ''}`}
                                                        onClick={() => {
                                                            setEditRating(v);
                                                            if (!editTouched) setEditTouched(true);
                                                        }}
                                                        aria-label={`Поставить ${v} из 5`}
                                                    >
                                                        <StarIcon filled={active} className="product-reviews__rate-star" />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {renderFieldError(editErrors.rating, editTouched)}
                                    </label>

                                    <label className="product-reviews__field product-reviews__field--wide">
                                        <span className="product-reviews__label">Текст</span>
                                        <textarea
                                            className="product-reviews__textarea"
                                            value={editText}
                                            onChange={(e) => setEditText(cut(e.target.value, LIMITS.textMax))}
                                            placeholder={`Минимум ${LIMITS.textMin} символов. Например: “Понравилась плотность и качество шва…”`}
                                            rows={4}
                                        />
                                        {renderCounter(editText, LIMITS.textMax)}
                                        {renderFieldError(editErrors.text, editTouched)}
                                    </label>

                                    <label className="product-reviews__field">
                                        <span className="product-reviews__label">Плюсы</span>
                                        <input
                                            className="product-reviews__input"
                                            value={editPros}
                                            onChange={(e) => setEditPros(cut(e.target.value, LIMITS.prosMax))}
                                            placeholder="Например: мягкий, приятный на ощупь"
                                        />
                                        {renderCounter(editPros, LIMITS.prosMax)}
                                        {renderFieldError(editErrors.pros, editTouched)}
                                    </label>

                                    <label className="product-reviews__field">
                                        <span className="product-reviews__label">Минусы</span>
                                        <input
                                            className="product-reviews__input"
                                            value={editCons}
                                            onChange={(e) => setEditCons(cut(e.target.value, LIMITS.consMax))}
                                            placeholder="Например: не нашёл"
                                        />
                                        {renderCounter(editCons, LIMITS.consMax)}
                                        {renderFieldError(editErrors.cons, editTouched)}
                                    </label>
                                </div>
                                <div className="product-reviews__form-actions">
                                    <button
                                        type="submit"
                                        className="product-reviews__btn product-reviews__btn--primary"
                                        disabled={!editIsValid}
                                        title={!editIsValid ? 'Проверьте поля формы' : undefined}
                                    >
                                        Сохранить
                                    </button>
                                    <button type="button" className="product-reviews__btn" onClick={() => setIsEditOpen(false)}>
                                        Отмена
                                    </button>
                                    <div className="product-reviews__form-note">После сохранения отзыв снова уйдёт “На модерацию”.</div>
                                </div>
                            </form>
                        )}
                        {/* Дополнение к отзыву: только published */}
                        {isUpdateFormOpen && hasMyReview && canAddUpdate && (
                            <form className="product-reviews__form" onSubmit={onSubmitUpdateFake} onChange={() => !updateTouched && setUpdateTouched(true)}>
                                <div className="product-reviews__form-title">Дополнение к отзыву</div>
                                <div className="product-reviews__form-note">Добавьте короткое обновление: как изменилось впечатление со временем.</div>{' '}
                                <div className="product-reviews__grid">
                                    <label className="product-reviews__field product-reviews__field--wide">
                                        <span className="product-reviews__label">Что изменилось со временем?</span>
                                        <textarea
                                            className="product-reviews__textarea"
                                            value={draftUpdateText}
                                            onChange={(e) => setDraftUpdateText(cut(e.target.value, LIMITS.updateMax))}
                                            placeholder={`Минимум ${LIMITS.updateMin} символов. Например: “Спустя 2 недели использования…”`}
                                            rows={3}
                                        />
                                        {renderCounter(draftUpdateText, LIMITS.updateMax)}
                                        {renderFieldError(updateErrors.update, updateTouched)}
                                    </label>
                                </div>
                                <div className="product-reviews__form-actions">
                                    <button
                                        type="submit"
                                        className="product-reviews__btn product-reviews__btn--primary"
                                        disabled={!updateIsValid}
                                        title={!updateIsValid ? 'Проверьте текст дополнения' : undefined}
                                    >
                                        Добавить
                                    </button>
                                    <button type="button" className="product-reviews__btn" onClick={() => setIsUpdateFormOpen(false)}>
                                        Отмена
                                    </button>
                                    <div className="product-reviews__form-note">Дополнение будет опубликовано сразу после модерации.</div>
                                </div>
                            </form>
                        )}
                    </div>
                    {count > 0 && (
                        <div className="product-reviews__listbar">
                            <span className="product-reviews__listbar-title">Все отзывы</span>
                            <label className="product-reviews__sort">
                                <span className="product-reviews__sort-label">Сортировка:</span>
                                <select
                                    className="product-reviews__sort-select"
                                    value={sortMode}
                                    onChange={(e) => setSortMode((e.target.value as SortMode) || 'helpful')}
                                    aria-label="Сортировка отзывов"
                                >
                                    <option value="helpful">Сначала полезные</option>
                                    <option value="new">Сначала новые</option>
                                </select>
                            </label>
                        </div>
                    )}
                    <div className="product-reviews__listwrap">
                        {/* Публичные отзывы */}
                        {publicReviews.length > 0 ? (
                            <div className="product-reviews__list" role="list">
                                {publicReviews.map((r) => (
                                    <article key={r.id} className="product-reviews__card" role="listitem">
                                        <div className="product-reviews__card-head">
                                            <div className="product-reviews__author">
                                                <span className="product-reviews__author-name">{r.author}</span>
                                                {/* {r.isVerifiedPurchase && <span className="product-reviews__badge">Покупка подтверждена</span>} */}
                                            </div>
                                            <div className="product-reviews__right">
                                                <RatingStars value={r.rating} className="product-reviews__stars" ariaLabel={`Оценка ${r.rating} из 5`} />
                                                <time className="product-reviews__date" dateTime={r.createdAt}>
                                                    {formatDateRu(r.createdAt)}
                                                </time>
                                            </div>
                                        </div>
                                        {(r.pros || r.cons) && (
                                            <div className="product-reviews__pc">
                                                {r.pros && (
                                                    <div className="product-reviews__pc-row">
                                                        <span className="product-reviews__pc-label">Плюсы:</span>
                                                        <span className="product-reviews__pc-value">{r.pros}</span>
                                                    </div>
                                                )}
                                                {r.cons && (
                                                    <div className="product-reviews__pc-row">
                                                        <span className="product-reviews__pc-label">Минусы:</span>
                                                        <span className="product-reviews__pc-value">{r.cons}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="product-reviews__text">{r.text}</div>
                                        {r.shopReply ? (
                                            <div className="product-reviews__shop-reply">
                                                <div className="product-reviews__shop-reply-title">
                                                    Ответ магазина
                                                    <span className="product-reviews__shop-reply-date">{formatDateRu(r.shopReply.createdAt)}</span>
                                                </div>
                                                <div className="product-reviews__shop-reply-text">{r.shopReply.text}</div>
                                            </div>
                                        ) : null}
                                        {renderReviewUpdates(r.updates)}
                                        {renderHelpfulness(r, myVotes[r.id] ?? null, false)}
                                    </article>
                                ))}
                            </div>
                        ) : (
                            <div className="product-reviews__empty">
                                <div className="product-reviews__empty-title">Отзывов пока нет</div>
                                <div className="product-reviews__empty-text">{renderEligibilityHint() || 'Расскажите, как вам товар - это помогает другим.'}</div>
                                {renderActions()}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Попап авторизации/регистрации (справа) */}
            <SidePopup open={authPopupOpen} onClose={closeAuthPopup} className="headerAuthPopup" showCloseButton={false}>
                <AuthPopupContent onClose={closeAuthPopup} />
            </SidePopup>
        </div>
    );
};

export default ProductReviews;
